import { Container, YStack, Text, Image, H1, Paragraph, H2 } from '@holo/ui';
import SEO from '@holo/seo';
import { Link } from '@holo/link';
import type { ProductQuery } from '@holo/gql';
import ProductControl from '@/components/ProductControl';

type Props = {
  data: ProductQuery;
  type: 'retail' | 'wholesale';
};

const Product = ({ data, type }: Props) => {
  // Remove null values
  const locationParts = [data.product.producer?.city, data.product.producer?.province].filter(Boolean);

  return (
    <>
      <SEO
        title={data.product.name}
        description={data.product.description || ''}
        openGraph={{
          type: 'website',
          title: data.product.name,
          description: data.product.description || '',
          images: [
            {
              url: data.product?.producer?.logo?.src || data.product?.image?.src,
              width: '800',
              height: '600',
              alt: data.product.name,
            },
          ],
        }}
      />

      <Container>
        <YStack
          flex={1}
          space="$8"
          $sm={{
            mt: '$12',
            flexDirection: 'row',
          }}
        >
          <Image
            quality={80}
            src={data.product?.image?.src || ''}
            alt={data.product?.name || 'Product image'}
            blurDataURL={data.product?.image?.base64}
            priority
            width={800}
            height={800}
            style={{
              width: 'auto',
              height: '100%',
              objectFit: 'cover',
            }}
            wrapperProps={{
              mx: -16,
              aspectRatio: 1,
              borderRadius: 0,
              overflow: 'hidden',

              $sm: {
                mx: 'unset',
                borderRadius: '$8',
                maxHeight: 'unset',
                flexBasis: '41%',
                justifyContent: 'center',
              },
            }}
            objectFit="cover"
          />

          <YStack
            flex={1}
            ai="flex-start"
            $sm={{
              justifyContent: 'center',
            }}
          >
            <Link href={`/${data.product.producer?.slug}`}>
              <Image
                src={data.product?.producer?.logo?.src || data.product?.producer?.image?.src || ''}
                alt={data.product?.name || 'Producer'}
                blurDataURL={data.product?.producer?.logo?.base64 || data.product?.producer?.image?.base64}
                width={50}
                height={50}
                borderRadius="50%"
              />
            </Link>

            <YStack mt="$2">
              <Text fontSize="$16">
                <Link href={`/${data.product.producer?.slug}`}>
                  <Text textDecorationLine="underline">{data.product.producer?.name}</Text>
                </Link>
                {locationParts.length > 0 && <> &bull; </>}
                {locationParts.length > 0 && <Text>{locationParts.join(', ')}</Text>}
              </Text>
              <H1 mt="$4">{data.product.name}</H1>
              {data.product.description && <Paragraph mt="$4">{data.product.description}</Paragraph>}
            </YStack>

            {data.product.variants && data.product.variants.length > 0 && (
              <ProductControl
                // @ts-ignore
                variants={data.product.variants
                  .filter((v) => v.active[type])
                  .map((v) => v?.price && { ...v, price: v.price[type] })}
                name={data.product.name}
                category={data.product.category?.name || ''}
                subCategory={data.product.subCategory?.name || ''}
                image={data.product.image?.src as string}
              />
            )}
          </YStack>
        </YStack>
      </Container>

      {data?.product?.producer?.description && data.product.producer.description[type] && (
        <YStack borderTopWidth={1} borderColor="$border" mt="$18" $sm={{ mt: 8 * 15 }}>
          <Container>
            <YStack space="$4" mt="$12" $sm={{ flexDirection: 'row' }} mb="$10">
              <H2 $sm={{ flexBasis: '33%' }}>{data.product.producer?.name}</H2>
              <Paragraph fontSize="$14" lineHeight={24} $sm={{ flex: 1, flexBasis: 0 }}>
                {data.product.producer?.description[type]}
              </Paragraph>
            </YStack>
          </Container>
        </YStack>
      )}
    </>
  );
};

export default Product;
